import React from "react";
import Select, { components } from "react-select";
import { RequiredSign } from "../../Constant/common";

/* Custom Input — adds data-rs-input so global CSS excludes it */
const CustomInput = (props) => <components.Input {...props} data-rs-input="true" />;

/* Shared style builder — uses CSS variables, fully theme-aware */
const buildStyles = (isInvalid) => ({
  control: (_, state) => ({
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    backgroundColor: "var(--input-bg)",
    border: `1px solid ${
      isInvalid
        ? "var(--danger)"
        : state.isFocused
          ? "var(--input-border-focus)"
          : "var(--input-border)"
    }`,
    borderRadius: 12,
    minHeight: 40,
    height: 40,
    boxShadow: state.isFocused
      ? isInvalid
        ? "0 0 0 3px rgba(244,63,94,0.1)"
        : "0 0 0 3px rgba(99,102,241,0.12)"
      : "none",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    pointerEvents: "auto",
    transition: "border-color 0.2s, box-shadow 0.2s",
  }),
  valueContainer: () => ({
    display: "flex",
    alignItems: "center",
    flex: 1,
    flexWrap: "wrap",
    padding: "0 10px",
    backgroundColor: "transparent",
    overflow: "hidden",
    height: "100%",
    position: "relative",
  }),
  input: () => ({
    backgroundColor: "transparent",
    color: "var(--text-primary)",
    margin: 0,
    padding: 0,
    fontSize: "0.8125rem",
    fontFamily: "Inter, sans-serif",
    gridArea: "1 / 2",
    minWidth: 2,
    border: 0,
    outline: 0,
    position: "relative",
    zIndex: 1,
  }),
  singleValue: () => ({
    color: "var(--text-primary)",
    fontSize: "0.8125rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    maxWidth: "calc(100% - 8px)",
  }),
  placeholder: () => ({
    color: "var(--text-muted)",
    fontSize: "0.8125rem",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "calc(100% - 8px)",
  }),
  indicatorsContainer: () => ({
    display: "flex",
    alignItems: "center",
    backgroundColor: "transparent",
    flexShrink: 0,
  }),
  indicatorSeparator: () => ({
    width: 1,
    backgroundColor: "var(--input-border)",
    alignSelf: "stretch",
    margin: "8px 0",
  }),
  dropdownIndicator: (_, state) => ({
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
    color: "var(--text-muted)",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.2s",
  }),
  clearIndicator: () => ({
    display: "flex",
    alignItems: "center",
    padding: "0 4px",
    color: "var(--text-muted)",
    cursor: "pointer",
  }),
  menu: () => ({
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: "var(--dropdown-bg)",
    border: "1px solid var(--dropdown-border)",
    borderRadius: 12,
    boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
    zIndex: 9999,
    overflow: "hidden",
  }),
  menuList: () => ({
    padding: 4,
    maxHeight: 260,
    overflowY: "auto",
  }),
  option: (_, state) => ({
    backgroundColor: state.isSelected
      ? "rgba(99,102,241,0.15)"
      : state.isFocused
        ? "var(--select-option-hover)"
        : "transparent",
    color: state.isSelected ? "var(--brand-indigo)" : "var(--text-primary)",
    fontSize: "0.8125rem",
    fontWeight: state.isSelected ? 600 : 400,
    padding: "9px 12px",
    borderRadius: 8,
    cursor: "pointer",
  }),
  noOptionsMessage: () => ({
    color: "var(--text-muted)",
    fontSize: "0.8125rem",
    padding: "10px 12px",
    textAlign: "center",
  }),
  groupHeading: () => ({
    fontSize: "0.625rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "var(--brand-indigo)",
    padding: "8px 12px 4px",
    background: "rgba(99,102,241,0.04)",
    borderRadius: 6,
    margin: "4px 0 2px",
  }),
  multiValue: () => ({
    display: "flex",
    alignItems: "center",
    backgroundColor: "rgba(99,102,241,0.12)",
    borderRadius: 6,
    margin: 2,
  }),
  multiValueLabel: () => ({
    color: "var(--brand-indigo)",
    fontSize: "0.75rem",
    padding: "2px 6px",
  }),
  multiValueRemove: () => ({
    display: "flex",
    alignItems: "center",
    padding: "0 4px",
    color: "var(--brand-indigo)",
    cursor: "pointer",
    borderRadius: "0 6px 6px 0",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

const BaseSelect = ({
  id,
  label,
  name,
  className,
  options,
  placeholder,
  handleChange,
  handleBlur,
  value,
  touched,
  error,
  isDisabled,
  required,
  mb,
  isLoading,
}) => {
  // Support both flat options and grouped options ({ label, options: [...] })
  const flatOptions = options?.flatMap((o) => (o.options ? o.options : [o])) || [];
  const selectedValue = flatOptions.find((o) => o.value === value) || null;
  const isInvalid = !!(touched && error);

  const handleChangeWrapper = (selected) => {
    handleChange(name, selected ? selected.value : "");
  };

  return (
    <div className={mb || "mb-3"}>
      {label && (
        <label htmlFor={id || name} className="form-label">
          {label}
          {required && <span className="text-danger ms-1">{RequiredSign}</span>}
        </label>
      )}
      <Select
        inputId={id || name}
        name={name}
        className={className}
        unstyled
        styles={buildStyles(isInvalid)}
        components={{ Input: CustomInput }}
        options={options}
        placeholder={placeholder || `Select ${label || ""}…`}
        value={selectedValue}
        onChange={handleChangeWrapper}
        onBlur={handleBlur}
        isDisabled={isDisabled}
        isLoading={isLoading}
        menuPortalTarget={document.body}
      />
      {isInvalid && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

export default BaseSelect;
